import React from 'react';

import { useTranslation } from 'react-i18next';

import Button from '../../Button';
import BillComId from './BillComId';
import ArrayField from './ArrayField';

import renderFieldGroup from './helpers/renderFieldsGroup';
import useListDragAndDrop from '../../../hooks/useListDragAndDrop';

export default function FormContainer({
  form,
  noSubmit,
  isVisible,
  groupSizeOffset = 0,
  tabindex,
  disabled,
  gridStyle,
  isSubmitting,
  readOnly,
  headerRight,
  noBillComId,
  title,
  submitButtonLabel,
  titleStyle,
  inlineSubmitButton,
  isSettingsForm,
  submitButtonStyle,
  noTitle,
}) {
  const arrayFieldName = form.fields.find(
    (field) => field.type === 'array'
  )?.name;

  const changeArrayFieldsOrder = (newArray) => {
    form.formik.setFieldValue(arrayFieldName, newArray);
  };

  const {
    disableDragOver,
    changeListItemOrder,
    listContainerRef,
    dragListItem,
  } = useListDragAndDrop({
    itemHeight: 80,
    itemsGap: 32,
    itemsList: form.formik.values[arrayFieldName],
    onItemsOrderChange: changeArrayFieldsOrder,
  });

  const { t } = useTranslation();

  return (
    <form
      className="flex flex-col gap-8"
      onSubmit={form.formik.handleSubmit}
      noValidate
    >
      {form.fields.map((fieldGroup, index) => {
        let shouldRender = true;

        if (fieldGroup.isHidden && fieldGroup.isHidden(form.formik.values)) {
          shouldRender = false;
        }

        if (!shouldRender) {
          return null;
        }

        const isArray = fieldGroup.type === 'array';
        // eslint-disable-next-line no-shadow
        const arrayFieldName = fieldGroup.name;

        // TODO: Refactor this to a separate component
        // If there are multiple array fields with drag and drop within a single form, it won't work with current implementation
        if (isArray) {
          if (
            form.formik?.values?.[arrayFieldName] &&
            form.formik.values[arrayFieldName].length
          ) {
            return (
              <div
                className="flex flex-col gap-6"
                ref={listContainerRef}
                onDragOver={disableDragOver}
                onDrop={changeListItemOrder}
              >
                {form.formik.values[arrayFieldName].map((vl, i) => (
                  <ArrayField
                    form={form}
                    fieldGroup={fieldGroup}
                    index={index}
                    i={i}
                    isSettingsForm={isSettingsForm}
                    isVisible={isVisible}
                    tabindex={tabindex}
                    gridStyle={gridStyle}
                    readOnly={readOnly}
                    groupSizeOffset={groupSizeOffset}
                    title={title}
                    isSubmitting={isSubmitting}
                    disabled={disabled}
                    submitButtonLabel={submitButtonLabel}
                    inlineSubmitButton={inlineSubmitButton}
                    isLast={i === form.formik.values[arrayFieldName].length - 1}
                    isFirst={i === 0}
                    dragListItem={dragListItem}
                    noteComponent={fieldGroup.noteComponent}
                    noteComponentProps={fieldGroup.noteComponentProps}
                  />
                ))}
              </div>
            );
          }

          // If no array items, render one empty array item so add button is visible
          return readOnly ? null : (
            <ArrayField
              form={form}
              fieldGroup={fieldGroup}
              index={index}
              i={0}
              isSettingsForm={isSettingsForm}
              isVisible={isVisible}
              tabindex={tabindex}
              gridStyle={gridStyle}
              readOnly={readOnly}
              groupSizeOffset={groupSizeOffset}
              title={title}
              isSubmitting={isSubmitting}
              disabled={disabled}
              submitButtonLabel={submitButtonLabel}
              inlineSubmitButton={inlineSubmitButton}
              empty
              isLast
              noteComponent={fieldGroup.noteComponent}
              noteComponentProps={fieldGroup.noteComponentProps}
            />
          );
        }

        return renderFieldGroup({
          fieldGroup,
          form,
          isVisible,
          tabindex,
          gridStyle,
          readOnly,
          headerRight,
          groupSizeOffset,
          title,
          isSubmitting,
          disabled,
          submitButtonLabel,
          titleStyle,
          inlineSubmitButton,
          index,
          isSettingsForm,
          noTitle,
        });
      })}
      {!noBillComId && <BillComId form={form} />}

      {!noSubmit && !readOnly && !inlineSubmitButton && (
        <div className="flex justify-between md:flex-row flex-col gap-2">
          <Button
            disabled={disabled}
            type="submit"
            isSubmitting={isSubmitting}
            className={submitButtonStyle}
          >
            {submitButtonLabel || t('common.Submit')}
          </Button>
        </div>
      )}
    </form>
  );
}
